import { useEffect, useMemo, useState, type ReactNode } from "react";
import { todayLocalISO } from "@workspace/shared";
import { useGameLibraryAdapter } from "../GameLibrary/AdapterContext.tsx";
import {
  gradeTone,
  localDay,
  readGradingSettings,
  seasonOf,
  tallyPlayers,
  type GradeTone,
  type GradingRow,
  type GradingSettings,
} from "./gradingStats.ts";
import { COORDINATOR } from "./gradingUnits.ts";

/** What both grading reports are given by the app. */
export interface GradingReportProps {
  /** The current game's plays (the app's local buffer). */
  plays: readonly GradingRow[];
  /** The app's Settings fields (Production, Correctables…). */
  fields: readonly { name?: unknown; label?: unknown; values?: unknown }[];
  /** The reports switcher (Jersey Report, Production Summary, Unit, Detail). */
  menubar?: ReactNode;
}

/** Today ("2026-10-01"), turning over at midnight while a report stays open. */
function useToday(): string {
  const [today, setToday] = useState(todayLocalISO);
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timer = setTimeout(() => setToday(todayLocalISO()), midnight.getTime() - now.getTime() + 1000);
    return () => clearTimeout(timer);
  }, [today]);
  return today;
}

/** This app's part of the reports, and the seasons to offer. */
export function useGradingReportData(plays: readonly GradingRow[], fields: GradingReportProps["fields"]) {
  const adapter = useGameLibraryAdapter();
  const settings: GradingSettings = useMemo(() => readGradingSettings(fields), [fields]);
  const currentRows = useMemo(() => plays.filter((row) => row && row.isDeleted !== true), [plays]);
  const today = useToday();
  const gamesKnown = adapter.games != null;
  const coordinator = adapter.appKey === COORDINATOR;

  // The seasons: this one and the last, and any other this app has
  // practices in. The report opens on the newest this app has practices in
  // (in January, last fall's) — once its library has loaded: until then the
  // season isn't known, and none is read (offline, the iPad's is).
  const { seasons, newest } = useMemo(() => {
    const years = new Set<number>();
    for (const g of adapter.games ?? []) {
      const year = g.game_id != null ? seasonOf(g.game_date ?? null) : null;
      if (year != null) years.add(year);
    }
    // The practice in progress: the day most of its plays were entered for, else today.
    if (currentRows.length > 0) {
      const year = seasonOf(localDay(tallyPlayers(currentRows).practiceDate) ?? today);
      if (year != null) years.add(year);
    }
    const thisYear = seasonOf(today) ?? new Date().getFullYear();
    const newest = years.size > 0 ? Math.max(...years) : thisYear;
    years.add(thisYear);
    years.add(thisYear - 1);
    return { seasons: [...years].sort((a, b) => b - a), newest };
  }, [adapter.games, currentRows, today]);

  return {
    appKey: adapter.appKey,
    /** The Defensive Coordinator's: a reports app, every unit's. */
    coordinator,
    isOnline: adapter.isOnline,
    /** False until the library's games have loaded. */
    gamesKnown,
    settings,
    currentRows,
    today,
    seasons,
    /**
     * The season to open on; null while the library loads (online). The
     * coordinator's library is no guide — it saves nothing now (client
     * 2026-11-02): this year (see useReportSeason).
     */
    defaultSeason: coordinator ? (seasonOf(today) ?? new Date().getFullYear()) : gamesKnown || !adapter.isOnline ? newest : null,
  };
}

type Opening = "try-this" | "try-last" | "this" | "last";

/**
 * The season a report shows: the one picked, else the one it opens on. A
 * position app opens on its library's newest; the coordinator's on this
 * year, or on last year while this one has no practice yet (January) — and
 * with neither, on this year (audit 2026-11-02). See useSettleOpening.
 */
export function useReportSeason(defaultSeason: number | null, coordinator: boolean) {
  const [picked, pick] = useState<number | null>(null);
  const [opening, setOpening] = useState<Opening>(coordinator ? "try-this" : "this");
  const lastYear = opening === "try-last" || opening === "last";
  const season = picked ?? (defaultSeason != null && lastYear ? defaultSeason - 1 : defaultSeason);
  return { season, picked, pick, opening, setOpening };
}

/**
 * Told, once the season shown has loaded, whether it has practices: settles
 * the coordinator's opening season. True while it is moving to another
 * season — nothing should show until that one has loaded.
 */
export function useSettleOpening(
  { picked, opening, setOpening }: ReturnType<typeof useReportSeason>,
  loaded: boolean,
  empty: boolean,
): boolean {
  const next: Opening | null =
    picked != null || !loaded
      ? null
      : opening === "try-this"
        ? empty
          ? "try-last"
          : "this"
        : opening === "try-last"
          ? empty
            ? "this"
            : "last"
          : null;
  useEffect(() => {
    if (next) setOpening(next);
  }, [next, setOpening]);
  return picked == null && loaded && empty && (opening === "try-this" || opening === "try-last");
}

/* ── Grade colours ── */

export const TONE_BOX: Record<GradeTone | "none", string> = {
  good: "border-green-300 bg-green-50 text-green-800",
  ok: "border-yellow-300 bg-yellow-50 text-yellow-800",
  low: "border-red-300 bg-red-50 text-red-700",
  none: "border-gray-200 bg-white text-gray-900",
};

export const TONE_CELL: Record<GradeTone | "none", string> = {
  good: "bg-green-100 text-green-800",
  ok: "bg-yellow-100 text-yellow-800",
  low: "bg-red-100 text-red-700",
  none: "",
};

export const toneOf = (pct: number | null): GradeTone | "none" => gradeTone(pct) ?? "none";
